/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useEffect, useRef, useCallback } from 'react';
import useHttp from 'hooks/http.hook';
import { useSelector } from 'react-redux';
import { selectIsPatient } from 'store/slices/auth.slice';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { RoutesConstants } from "medicalsystem-shared";
import VideochatRenderer from 'components/common/VideochatRenderer';
import { RemoteParticipant } from '@azure/communication-calling';
import { 
  useWindowManager, 
  createHandleMouseDownWrapper
} from 'utils/windowSize.utils';
import { useMaterialUIController } from 'context/ThemeContext';
import { CoreAPI_URLs } from 'constants/http.const';
import { AzureCommunicationTokenInterface, HeaderEnum, JoinVideochatResponseInterface, VideochatSessionInterface } from 'medicalsystem-shared';
import { ChatMessageInterface } from 'medicalsystem-shared';

/**
 * Properties for the VideochatModal component.
 * 
 * @interface VideochatModalProps
 * @property {string} appointmentId - Appointment ID for videochat session
 * @property {string} patientName - Patient's full name
 * @property {string} providerName - Provider's full name
 * @property {boolean} isOpen - Whether modal is visible
 * @property {Function} onClose - Callback when modal is closed
 */
interface VideochatModalProps {
  appointmentId: string;
  patientName: string;
  providerName: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Videochat modal component for telemedicine appointments.
 * Full-featured video calling interface using Azure Communication Services.
 * 
 * Features:
 * - Azure Communication Services integration
 * - Audio/video calling with screen sharing
 * - Real-time participant management
 * - Video feed swapping between local and remote
 * - Camera and microphone selection
 * - Mute audio/video controls
 * - Integrated text chat with unread indicators
 * - Window management (minimize, maximize, resize, drag)
 * - Guest join URL generation with access code
 * - Session state management
 * - Connection error handling
 * - Call reconnection capability
 * - Dark mode support
 * - Responsive video layout
 * - Participant name display
 * - Call duration tracking
 * - End call for all participants (host)
 * 
 * Call Workflow:
 * 1. Initialize Azure Communication client
 * 2. Fetch or create videochat session
 * 3. Join group call
 * 4. Setup local video/audio streams
 * 5. Handle remote participants joining
 * 6. Enable chat messaging
 * 7. End call and cleanup resources
 * 
 * Session Management:
 * - Create session on first join
 * - Generate guest access codes
 * - Track session state (active/ended)
 * - Support session rejoin
 * - End session for all participants
 * 
 * Video Management:
 * - Local video preview
 * - Remote participant video
 * - Swap video positions
 * - Picture-in-picture mode
 * - Video device selection
 * - Video quality adjustment
 * 
 * Audio Management:
 * - Microphone mute/unmute
 * - Audio device selection
 * - Remote participant audio
 * - Audio level indicators
 * 
 * Chat Features:
 * - Real-time text messaging
 * - Participant names in messages
 * - Unread message counter
 * - Chat panel toggle
 * - Message history
 * - Timestamp display
 * 
 * Window Features:
 * - Draggable modal window
 * - Resizable dimensions
 * - Minimize to corner
 * - Maximize to fullscreen
 * - Restore to default size
 * - Custom window controls
 * 
 * Permissions:
 * - Patient and provider access
 * - Camera/microphone permissions
 * - Guest join capability
 * - Host controls (end call for all)
 * 
 * Error Handling:
 * - Connection failures
 * - Device access errors
 * - Session expiry
 * - Network issues
 * - Graceful degradation
 * 
 * @param {VideochatModalProps} props - Component properties
 * @returns {JSX.Element} Videochat modal with call interface
 */
const VideochatModal: React.FC<VideochatModalProps> = ({
  appointmentId,
  patientName,
  providerName,
  isOpen,
  onClose
}) => {
  const [isLoading, error, sendRequest] = useHttp();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [callAgent, setCallAgent] = useState<any>(null);
  const [deviceManager, setDeviceManager] = useState<any>(null);
  const [call, setCall] = useState<any>(null);
  const [callState, setCallState] = useState<string>('None');
  const [participants, setParticipants] = useState<RemoteParticipant[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [cameras, setCameras] = useState<any[]>([]);
  const [microphones, setMicrophones] = useState<any[]>([]);
  const [session, setSession] = useState<VideochatSessionInterface | null>(null);
  const [groupId, setGroupId] = useState<string>('');
  const [accessCode, setAccessCode] = useState<string>('');
  const [guestJoinUrl, setGuestJoinUrl] = useState<string>('');
  const [connectionError, setConnectionError] = useState<string>('');
  const [callHasEnded, setCallHasEnded] = useState(false);
  const [canRejoin, setCanRejoin] = useState(false);
  const [hasLeftCall, setHasLeftCall] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isEndingCall, setIsEndingCall] = useState(false);
  const [isTestMode] = useState(process.env.NODE_ENV === 'development');
  const localVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoMinimizedRef = useRef<HTMLDivElement>(null);
  const [currentRemoteStream, setCurrentRemoteStream] = useState<any>(null);
  const [currentRemoteRenderer, setCurrentRemoteRenderer] = useState<any>(null);
  const [currentLocalStream, setCurrentLocalStream] = useState<any>(null);
  const [currentLocalRenderer, setCurrentLocalRenderer] = useState<any>(null);
  const minimizedVideoRef = useRef<HTMLDivElement>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessageInterface[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [forceRender, setForceRender] = useState(0);
  const [participantNames, setParticipantNames] = useState<{ [key: string]: string }>({});
  const isPatient = useSelector(selectIsPatient);
  const currentUserName = isPatient ? `${patientName} (Patient)` : `${providerName} (Provider)`;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isVideoSwapped, setIsVideoSwapped] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState<any>(null);
  const prevMinimizedRef = useRef(false);
  const windowRef = useRef<HTMLDivElement>(null);
  const isAuthenticated = useSelector((state: { auth: { isAuthenticated: boolean } }) => state.auth.isAuthenticated);
  const [isSwapping, setIsSwapping] = useState(false);
  const {
    state: windowState,
    handleMouseDown,
    handleMinimizedMouseDown,
    handleResizeMouseDown,
    toggleMaximize,
    minimize,
    restore,
    maximize
  } = useWindowManager();

  const handleNewProgressNote = () => {
    if (windowState.isMaximized) {
      restore();
    }
    const data = appointmentId;
    navigate(RoutesConstants.PROGRESS_NOTE, { 
      state: { 
        data
      } 
    });
  };

  const handleMinimizedMouseDownWrapper = (e: React.MouseEvent) => {
    prevMinimizedRef.current = true;
    handleMinimizedMouseDown(e);
  };
  
  const swapVideoStreams = useCallback(() => {
    setIsVideoSwapped(prev => {
      const newState = !prev;
      return newState;
    });
  }, []);
  
  const checkMultipleCameras = useCallback(async (deviceManager: any) => {
    const cameras = await deviceManager.getCameras();
    setCameras(cameras);
    
    if (cameras.length > 1) {
    } else if (cameras.length > 0) {
      setSelectedCamera(cameras[0]);
    }
  }, []);
  
  const handleMouseDownWrapper = createHandleMouseDownWrapper(windowState, handleMouseDown, windowRef);

  const handleResizeMouseDownWrapper = (e: React.MouseEvent, direction: string) => {
    handleResizeMouseDown(e, direction);
  };

  const getMainScreenParticipantName = useCallback(() => {
    if (participants.length === 0) return '';
    if (isVideoSwapped) {
      return t('videochat.you');
    }
    return isPatient ? providerName : patientName;
  }, [participants.length, isPatient, providerName, patientName, isVideoSwapped, t]);

  const initializeACS = useCallback(async (token: AzureCommunicationTokenInterface) => {
    try {
      setIsInitializing(true);
      setSelectedCamera(null);
      
      if (!token || !token.token) {
        throw new Error('Invalid or missing Azure Communication Services token');
      }

      const { CallClient } = await import('@azure/communication-calling');
      const { AzureCommunicationTokenCredential } = await import('@azure/communication-common');
      const tokenCredential = new AzureCommunicationTokenCredential(token.token);
      const callClient = new CallClient();
      const callAgentInstance = await callClient.createCallAgent(tokenCredential, {
        displayName: currentUserName
      });
      
      const deviceManagerInstance = await callClient.getDeviceManager();
      setCallAgent(callAgentInstance);
      setDeviceManager(deviceManagerInstance);
      const cameras = await deviceManagerInstance.getCameras();
      const microphones = await deviceManagerInstance.getMicrophones();
      
      setCameras(cameras);
      setMicrophones(microphones);
      
      await checkMultipleCameras(deviceManagerInstance);
      return { callAgent: callAgentInstance, deviceManager: deviceManagerInstance };
    } catch (err) {
      console.error('Error initializing ACS:', err);
      setConnectionError(t('videochat.acsInitializationError'));
      throw err;
    } finally {
      setIsInitializing(false);
    }
  }, [currentUserName, t]);

  const joinVideochat = useCallback(async () => {
    try {
      const response = await sendRequest<JoinVideochatResponseInterface>(
        CoreAPI_URLs.VIDEOCHAT_JOIN,
        (data) => {
          setSession(data.session);
          setGroupId(data.groupId);
        },
        'POST',
        JSON.stringify({
          displayName: currentUserName,
          providerNotes: `Videochat started by ${currentUserName}`
        }),
        [
          { header: HeaderEnum.RESOURCE_ID, value: appointmentId },
          { header: 'Content-Type', value: 'application/json' }
        ]
      );

      if (response?.data) {
        const acsComponents = await initializeACS(response.data.communicationToken);
        
        if (acsComponents.callAgent && response.data.groupId) {
          const localVideoStreams = isVideoOn ? await getLocalVideoStream(acsComponents.deviceManager) : [];
          
          try {
            const groupLocator = {
              groupId: response.data.groupId
            };
            const callOptions = {
              videoOptions: localVideoStreams.length > 0 ? {
                localVideoStreams: localVideoStreams
              } : undefined,
              audioOptions: {
                muted: false
              }
            };
            
            const groupCall = acsComponents.callAgent.join(groupLocator, callOptions);  
            setCall(groupCall);
            setCallState(groupCall.state);
            setupCallEventHandlers(groupCall);
            let stateCheckCount = 0;
            const maxStateChecks = 30;
            
            const stateMonitor = setInterval(() => {
              stateCheckCount++;             
              if (groupCall.state === 'Connected' || stateCheckCount >= maxStateChecks) {
                clearInterval(stateMonitor);
                if (groupCall.state === 'Connected') {
                } else {
                  console.warn(' Call did not connect within 30 seconds');
                }
              }
            }, 1000);
            
          } catch (joinError) {
            console.error('Error joining group call:', joinError);
            throw joinError;
          }
        } else {
          console.error('Missing required components:', {
            hasCallAgent: !!acsComponents.callAgent,
            hasGroupId: !!response.data.groupId
          });
          setConnectionError(t('videochat.missingCallAgentOrGroupId'));
        }
      }
    } catch (err) {
      console.error('Error joining videochat:', err);
      setConnectionError(t('videochat.joinVideochatError'));
    }
  }, [appointmentId, currentUserName, initializeACS, sendRequest, isVideoOn, isMuted, t]);

  const selectCameraWithPrompt = useCallback(async () => {
    if (cameras.length <= 1) {
      return cameras[0] || null;
    }
    return new Promise<any>((resolve) => {
      setCameraSelectionResolver(() => resolve);
      setShowCameraSelection(true);
    });
  }, [cameras]);

  const getLocalVideoStream = async (deviceManager: any) => {
    try {
      const cameraToUse = await selectCameraWithPrompt();
      if (cameraToUse) {
        const { LocalVideoStream } = await import('@azure/communication-calling');
        const localVideoStream = new LocalVideoStream(cameraToUse);
        
        setCurrentLocalStream(localVideoStream);
        return [localVideoStream];
      }
    } catch (err) {
      console.error('Error getting local video stream:', err);
    }
    return [];
  };

  const sendChatMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;
  
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
    const chatMessage: ChatMessageInterface = {
      id: messageId,
      sender: currentUserName,
      message: message.trim(),
      appointmentId: appointmentId,
      timestamp: new Date(),
      isOwn: true
    };
  
    try {
      setChatMessages(prev => [...prev, chatMessage]);
    
      await sendRequest(
        CoreAPI_URLs.VIDEOCHAT_SEND_MESSAGE,
        (data) => {
        },
        'POST',
        JSON.stringify({
          id: messageId,
          message: chatMessage.message,
          sender: chatMessage.sender,
          timestamp: chatMessage.timestamp.toISOString(),
          sessionId: session?.sessionId
        }),
        [
          { header: HeaderEnum.RESOURCE_ID, value: appointmentId },
          { header: 'Content-Type', value: HeaderEnum.CONTENT_TYPE }
        ],
        {
          successMessage: ''
        }
      );
    
    } catch (error) {
      setChatMessages(prev => prev.filter(msg => msg.id !== messageId));
      setConnectionError(t('videochat.chatMessageError') || 'Error enviando mensaje');
      setTimeout(() => {
        setConnectionError('');
      }, 3000);
    }
  }, [appointmentId, currentUserName, sendRequest, session?.sessionId, t]);

  const pollForMessagesRobust = useCallback(async () => {
    if (!session?.sessionId || callState !== 'Connected') return;
  
    try {
      await sendRequest(
        CoreAPI_URLs.VIDEOCHAT_GET_MESSAGES,
        (data: { messages: any[] }) => {
          if (data.messages && data.messages.length > 0) {
            setChatMessages(prev => {
              const existingMessages = new Map();
              prev.forEach(msg => {
                const key1 = msg.id;
                const key2 = `${msg.timestamp.getTime()}_${msg.sender}_${msg.message}`;
                existingMessages.set(key1, true);
                existingMessages.set(key2, true);
              });
            
              const incomingMessages = data.messages
                .map(msg => ({
                  ...msg,
                  timestamp: new Date(msg.timestamp),
                  isOwn: msg.sender === currentUserName
                }))
                .filter(msg => {
                  const key1 = msg.id;
                  const key2 = `${new Date(msg.timestamp).getTime()}_${msg.sender}_${msg.message}`;
                  return !existingMessages.has(key1) && !existingMessages.has(key2);
                });
            
              if (incomingMessages.length === 0) {
                return prev;
              }
            
              if (!isChatOpen) {
                const incomingFromOthers = incomingMessages.filter(msg => !msg.isOwn);
                if (incomingFromOthers.length > 0) {
                  setUnreadMessages(prevCount => prevCount + incomingFromOthers.length);
                }
              }
            
              const updatedMessages = [...prev, ...incomingMessages];
              return updatedMessages.sort((a, b) => 
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              );
            });
          }
        },
        'GET',
        undefined,
        [
          { header: HeaderEnum.RESOURCE_ID, value: appointmentId }
        ]
      );
    } catch (error) {
      console.error('Error polling for messages:', error);
    }
  }, [session?.sessionId, appointmentId, sendRequest, isChatOpen, currentUserName, callState]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
  
    if (session?.sessionId && callState === 'Connected') {
      pollInterval = setInterval(pollForMessagesRobust, 2000);
      pollForMessagesRobust();
    }
  
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [session?.sessionId, callState, pollForMessagesRobust]);

  const cleanupRenderers = useCallback(() => {
    if (currentRemoteRenderer) {
      try {
        currentRemoteRenderer.dispose();
      } catch (err) {
        console.warn('Error disposing remote renderer:', err);
      }
    }
  
    if (currentLocalRenderer) {
      try {
        currentLocalRenderer.dispose();
      } catch (err) {
        console.warn('Error disposing local renderer:', err);
      }
    }
  
    setCurrentRemoteRenderer(null);
    setCurrentLocalRenderer(null);
  }, [currentRemoteRenderer, currentLocalRenderer]);

  const setupCallEventHandlers = (call: any) => {
    call.on('stateChanged', () => {
      const newState = call.state;
    
      setCallState(newState);
    
      if (newState === 'Connected') {
      } else if (newState === 'Disconnected' || newState === 'Disconnecting') {
      
        if (call.callEndReason && call.callEndReason.code !== 0) {
          setConnectionError(t('videochat.callEndedWithError', { reason: call.callEndReason.phrase || 'Unknown error' }));
        }
        setTimeout(() => {
          if (callState === 'Disconnected') {
            setCall(null);
            setCallState('None');
            setCallHasEnded(true);
          }
        }, 2000);
      } else if (newState === 'Connecting') {
        setConnectionError('');
      } else if (newState === 'Ringing') {
      }
    });

    call.on('remoteParticipantsUpdated', async (e: any) => {     
      const remoteParticipants = Array.from(call.remoteParticipants.values())
        .filter((p: any) => p.identifier.kind === 'communicationUser' || p.identifier.kind === 'phoneNumber');
      setParticipants(remoteParticipants.map((participant: any) => participant));
      const newParticipantNames: { [key: string]: string } = {};
      remoteParticipants.forEach((participant: any) => {
        const userId = participant.identifier.communicationUserId || participant.identifier.phoneNumber;
        if (userId && participant.displayName) {
          newParticipantNames[userId] = participant.displayName;
        }
      });
      setParticipantNames(prev => ({ ...prev, ...newParticipantNames }));
      if (e.removed && e.removed.length > 0) {
        e.removed.forEach((participant: any) => {
          if (remoteVideoRef.current) {
            remoteVideoRef.current.innerHTML = '';
          }
          if (remoteVideoMinimizedRef.current) {
            remoteVideoMinimizedRef.current.innerHTML = '';
          }
          
          if (currentRemoteRenderer) {
            try {
              currentRemoteRenderer.dispose();
              setCurrentRemoteRenderer(null);
            } catch (err) {
              console.warn('Error disposing previous renderer:', err);
            }
          }
        });
        
        setForceRender(prev => prev + 1);
      }
      
      if (e.added && e.added.length > 0) {
        e.added.forEach((participant: any) => {
        });
      }
    });
  };

  const generateAccessCode = async () => {
    try {
      await sendRequest(
        CoreAPI_URLs.VIDEOCHAT_GENERATE_ACCESS_CODE,
        (data: { accessCode: string; joinUrl: string }) => {
          setAccessCode(data.accessCode);
          const baseUrl = window.location.origin;
          const guestUrl = `${baseUrl}${CoreAPI_URLs.VIDEOCHAT_GUEST}${data.accessCode}`;
          setGuestJoinUrl(guestUrl);
        },
        'POST',
        JSON.stringify({}),
        [
          { header: HeaderEnum.RESOURCE_ID, value: appointmentId },
          { header: 'Content-Type', value: 'application/json' }
        ]
      );
    } catch (err) {
      console.error('Error generating access code:', err);
    }
  };

  const copyGuestJoinUrl = () => {
    if (guestJoinUrl) {
      navigator.clipboard.writeText(guestJoinUrl);
    }
  };

  const copyAccessCode = () => {
    navigator.clipboard.writeText(accessCode);
  };
  const toggleMicrophone = async () => {
    if (call) {
      try {
        if (isMuted) {
          await call.unmute();
        } else {
          await call.mute();
        }
        setIsMuted(!isMuted);
      } catch (err) {
        console.error('Error toggling microphone:', err);
      }
    }
  };

  const toggleVideo = async () => {
    if (call && deviceManager) {
      try {
        
        const hasActiveVideoStreams = call.localVideoStreams && call.localVideoStreams.length > 0;
        
        if (hasActiveVideoStreams && isVideoOn) {
          await call.stopVideo(call.localVideoStreams[0]);
          setCurrentLocalStream(null); 
          setIsVideoOn(false);
        } else if (!hasActiveVideoStreams && !isVideoOn) {
          const localVideoStreams = await getLocalVideoStream(deviceManager);
          if (localVideoStreams.length > 0) {
            await call.startVideo(localVideoStreams[0]);
            setIsVideoOn(true);
          } else {
            console.warn('No local video streams available');
          }
        } else {
          if (hasActiveVideoStreams && !isVideoOn) {
            setIsVideoOn(true);
            if (call.localVideoStreams[0]) {
              setCurrentLocalStream(call.localVideoStreams[0]);
            }
          } else if (!hasActiveVideoStreams && isVideoOn) {
            setIsVideoOn(false);
            setCurrentLocalStream(null); 
          }
        }
      } catch (err) {
        console.error('Error toggling video:', err);
        
        const hasActiveVideoStreams = call.localVideoStreams && call.localVideoStreams.length > 0;
        if (hasActiveVideoStreams !== isVideoOn) {
          setIsVideoOn(hasActiveVideoStreams);
          if (hasActiveVideoStreams && call.localVideoStreams[0]) {
            setCurrentLocalStream(call.localVideoStreams[0]);
          } else {
            setCurrentLocalStream(null);
          }
        }
      }
    }
  };
  const handleEndCall = useCallback(async () => {
    if (hasLeftCall) {
      onClose();
      return;
    }
    try {
      if (call && typeof call.hangUp === 'function') {
        await call.hangUp();
        setCall(null);
        setCallState('None');
        setParticipants([]);
        setIsVideoOn(false);
        setIsMuted(false);
      }
      setHasLeftCall(true);
      setCallHasEnded(false); 
      setCanRejoin(false); 
    } catch (err) {
      console.error('Error ending call:', err);
    }
  }, [call, hasLeftCall, onClose]);

  const handleJoinCall = useCallback(async () => {
    if (!session) return;
    
    setHasLeftCall(false);
    setCallHasEnded(false);
    setConnectionError('');
    
    try {
      await joinVideochat();
    } catch (error) {
      console.error('Error joining call:', error);
      setConnectionError('Error joining the call');
      setHasLeftCall(true);
    }
  }, [session, joinVideochat]);

  const handleRejoinCall = useCallback(async () => {
    if (!session) return;
    
    setCallHasEnded(false);
    setCanRejoin(false);
    setHasLeftCall(false);
    setConnectionError('');
    
    try {
      await joinVideochat();
    } catch (error) {
      console.error('Error rejoining call:', error);
      setConnectionError('Error rejoining the call');
      setCallHasEnded(true);
      setCanRejoin(true);
    }
  }, [session, joinVideochat]);

  const endCall = async () => {
    if (isEndingCall) return;
    setIsEndingCall(true);
    if (call) {
      try {
        await call.hangUp();
        setCall(null);
        setCallState('None');
      } catch (err) {
        console.error('Error hanging up call:', err);
      }
    }
    
    try {
      await sendRequest(
        CoreAPI_URLs.VIDEOCHAT_END,
        () => {
          setSession(null);
          setParticipants([]);
        },
        'POST',
        JSON.stringify({}),
        [{ header: HeaderEnum.RESOURCE_ID, value: appointmentId }]
      );
    } catch (err) {
      console.error('Error ending session:', err);
    }
    
    onClose();
    setIsEndingCall(false);
  };

  const handleProgressNoteClick = () => {
    if (windowState.isMaximized) {
      restore();
    }
    const data = appointmentId;
    navigate(RoutesConstants.PROGRESS_NOTE, { 
      state: { 
        data
      } 
    });
  };
  
  useEffect(() => {
    if (!isAuthenticated && isOpen) {
      onClose();
    }
  }, [isAuthenticated, isOpen, onClose]);

  useEffect(() => {
    if (isOpen && !session) {
      joinVideochat();
      generateAccessCode();
    }
  }, [isOpen, session, joinVideochat]);

  useEffect(() => {
    const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const isPageReloading = performance.navigation?.type === 1 || 
                           navigationEntry?.type === 'reload';
    
    return () => {
      if (isPageReloading) {
        return;
      }
      
      if (call && typeof call.hangUp === 'function') {
        try {
          if (call.state && call.state !== 'Disconnected' && call.state !== 'Disconnecting') {
            call.hangUp().catch((err) => {
              console.warn('webRtcSession cleanup - Error during hangUp:', err);
            });
          }
        } catch (err) {
          console.warn('webRtcSession cleanup - Error accessing call state:', err);
        }
      }
      setIsVideoSwapped(false);
    };
  }, [call]);

  useEffect(() => {
    if (!windowState.isMinimized || !minimizedVideoRef.current) {
      return;
    }
    const renderMinimizedVideo = async () => {
      if (!minimizedVideoRef.current) return;
      minimizedVideoRef.current.innerHTML = '';
      try {
        if (participants.length > 0) {
          const participant = participants[0];
          
          if (participant.videoStreams && participant.videoStreams.length > 0) {
            const remoteStream = participant.videoStreams[0];
            
            if (remoteStream.isAvailable) {
              const { VideoStreamRenderer } = await import('@azure/communication-calling');
              const renderer = new VideoStreamRenderer(remoteStream);
              const view = await renderer.createView({ scalingMode: 'Crop' });
              
              if (minimizedVideoRef.current && windowState.isMinimized) {
                minimizedVideoRef.current.appendChild(view.target);
                setCurrentRemoteRenderer(prev => {
                  if (prev) {
                    try {
                      prev.dispose();
                    } catch (err) {
                      console.warn('Error disposing previous renderer:', err);
                    }
                  }
                  return renderer;
                });
              } else {
                renderer.dispose();
              }
            }
          }
        }
      } catch (error) {
        console.error('Error rendering minimized video:', error);
      }
    };
    renderMinimizedVideo();
    const handleVideoStreamUpdate = () => {
      if (windowState.isMinimized) {
        setTimeout(renderMinimizedVideo, 100);
      }
    };
    participants.forEach(participant => {
      participant.on('videoStreamsUpdated', handleVideoStreamUpdate);
    });
    return () => {
      participants.forEach(participant => {
        participant.off('videoStreamsUpdated', handleVideoStreamUpdate);
      });
    };

  }, [windowState.isMinimized, participants]);

  useEffect(() => {
    if (!windowState.isMinimized && currentRemoteRenderer) {
      try {
        currentRemoteRenderer.dispose();
        setCurrentRemoteRenderer(null);
      } catch (err) {
        console.warn('Error disposing renderer on restore:', err);
      }
    }
  }, [windowState.isMinimized, currentRemoteRenderer]);

  const [showCameraSelection, setShowCameraSelection] = useState(false);
  const [cameraSelectionResolver, setCameraSelectionResolver] = useState<((camera: any) => void) | null>(null);

  const handleCameraSelectionCancel = () => {
    const defaultCamera = cameras[0];
    setSelectedCamera(defaultCamera);
    setShowCameraSelection(false);
    if (cameraSelectionResolver) {
      cameraSelectionResolver(defaultCamera);
      setCameraSelectionResolver(null);
    }
  };

  const handleCameraSelect = (camera: any) => {
    setSelectedCamera(camera);
    setShowCameraSelection(false);
    if (cameraSelectionResolver) {
      cameraSelectionResolver(camera);
      setCameraSelectionResolver(null);
    }
  };

  if (!isOpen) return null;
  if (windowState.isMinimized) {
    return (
      <div
        ref={windowRef}
        data-window-ref="true"
        onMouseDown={handleMinimizedMouseDownWrapper}
        style={{
          position: 'fixed',
          left: windowState.minimizedPosition.x,
          top: windowState.minimizedPosition.y,
          width: '300px',
          height: '200px',
          zIndex: 9999,
          cursor: windowState.isDraggingMinimized ? 'grabbing' : 'grab',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          border: '2px solid #4caf50',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      >
        <VideochatRenderer
          call={call}
          callAgent={callAgent}
          deviceManager={deviceManager}
          callState={callState}
          participants={participants}
          currentUserName={currentUserName}
          getParticipantName={getMainScreenParticipantName}
          chatMessages={chatMessages}
          onSendMessage={sendChatMessage}
          isVideoEnabled={isVideoOn}
          isAudioEnabled={!isMuted}
          onToggleVideo={toggleVideo}
          onToggleAudio={toggleMicrophone}
          onEndCall={handleEndCall}
          showChat={true}
          showControls={false}
          showSwapButton={false}
          isMinimized={true}
          onRestore={restore}
          windowTitle={`Videochat - ${patientName || 'Paciente'}`}
          showWindowControls={true}
          className="modal-videochat-renderer"
          guestJoinUrl={guestJoinUrl}
          callHasEnded={callHasEnded}
          canRejoin={canRejoin}
          onRejoinCall={handleRejoinCall}
          onJoinCall={handleJoinCall}
          hasLeftCall={hasLeftCall}
        />
      </div>
    );
  }
  return (
    <div
      ref={windowRef}
      data-window-ref="true"
      onMouseDown={handleMouseDownWrapper}
      style={{
        position: 'fixed',
        left: windowState.isMaximized ? 0 : windowState.position.x,
        top: windowState.isMaximized ? 0 : windowState.position.y,
        width: windowState.isMaximized ? '100vw' : windowState.windowSize.width,
        height: windowState.isMaximized ? '100vh' : windowState.windowSize.height,
        backgroundColor: 'white',
        borderRadius: windowState.isMaximized ? '0px' : '12px',
        boxShadow: windowState.isMaximized ? 'none' : '0 8px 32px rgba(0,0,0,0.3)',
        zIndex: 9999,
        overflow: 'hidden',
        border: windowState.isMaximized ? 'none' : '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        cursor: windowState.isDragging ? 'grabbing' : 'grab',
        ...(windowState.isMaximized && {
          minWidth: '100vw',
          maxWidth: '100vw',
          minHeight: '100vh',
          maxHeight: '100vh',
          margin: 0,
          padding: 0,
          right: 0,
          bottom: 0,
          transform: 'none',
          inset: '0px'
        })
      }}
    >
      {!windowState.isMaximized && (
        <>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '10px',
              height: '10px',
              cursor: 'nw-resize',
              zIndex: 10001
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'top-left')}
          />
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: '10px',
              height: '10px',
              cursor: 'ne-resize',
              zIndex: 10001
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'top-right')}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '10px',
              height: '10px',
              cursor: 'sw-resize',
              zIndex: 10001
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-left')}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '10px',
              height: '10px',
              cursor: 'se-resize',
              zIndex: 10001
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'bottom-right')}
          />
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: '10px',
              right: '10px',
              height: '5px',
              cursor: 'n-resize',
              zIndex: 10001
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'top')}
          />
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: '10px',
              right: '10px',
              height: '5px',
              cursor: 's-resize',
              zIndex: 10001
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'bottom')}
          />
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '10px',
              bottom: '10px',
              width: '5px',
              cursor: 'w-resize',
              zIndex: 10001
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'left')}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '10px',
              bottom: '10px',
              width: '5px',
              cursor: 'e-resize',
              zIndex: 10001
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'right')}
          />
        </>
      )}
      <div
        style={{
          display: 'flex',
          gap: '0px',
          height: windowState.isMaximized ? '100vh' : '100%',
          flexShrink: 0,
          borderRadius: '8px',
          overflow: 'hidden',
          flex: 1
        }}
      >
        <VideochatRenderer
          call={call}
          callAgent={callAgent}
          deviceManager={deviceManager}
          callState={callState}
          participants={participants}
          currentUserName={currentUserName}
          getParticipantName={getMainScreenParticipantName}
          chatMessages={chatMessages}
          onSendMessage={sendChatMessage}
          isVideoEnabled={isVideoOn}
          isAudioEnabled={!isMuted}
          onToggleVideo={toggleVideo}
          onToggleAudio={toggleMicrophone}
          onEndCall={handleEndCall}
          showChat={false}
          showControls={true}
          showSwapButton={true}
          allowChatToggle={true}
          showProgressNoteButton={!isPatient}
          onProgressNoteClick={handleProgressNoteClick}
          isMinimized={windowState.isMinimized}
          isMaximized={windowState.isMaximized}
          onMinimize={minimize}
          onMaximize={maximize}
          onRestore={restore}
          windowTitle={`Videochat - ${patientName || 'Paciente'}`}
          showWindowControls={true}
          className="modal-videochat-renderer"
          guestJoinUrl={guestJoinUrl}
          callHasEnded={callHasEnded}
          canRejoin={canRejoin}
          onRejoinCall={handleRejoinCall}
          onJoinCall={handleJoinCall}
          hasLeftCall={hasLeftCall}
          isGuestContext={false}
        />
      </div>
      {showCameraSelection && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <div
            style={{
              backgroundColor: darkMode ? '#1a1a1a' : 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              minWidth: '300px',
              maxWidth: '400px',
              border: darkMode ? '1px solid #333' : '1px solid #e0e0e0'
            }}
          >
            <h3 style={{ 
              margin: '0 0 20px 0', 
              color: darkMode ? '#fff' : '#333',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              {t('videochat.selectCamera')}
            </h3>
            <div style={{ marginBottom: '20px' }}>
              {cameras.map((camera, index) => (
                <button
                  key={camera.id || index}
                  onClick={() => handleCameraSelect(camera)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '12px 16px',
                    margin: '8px 0',
                    backgroundColor: darkMode ? '#2c2c2c' : '#f8f9fa',
                    color: darkMode ? '#fff' : '#333',
                    border: darkMode ? '1px solid #444' : '1px solid #dee2e6',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode ? '#404040' : '#e9ecef';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode ? '#2c2c2c' : '#f8f9fa';
                  }}
                >
                  {camera.name || `${t('videochat.camera')} ${index + 1}`}
                </button>
              ))}
            </div>
            <button 
              onClick={handleCameraSelectionCancel}
              style={{
                width: '100%',
                padding: '10px 16px',
                backgroundColor: darkMode ? '#444' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default VideochatModal;